'use client';
import React, { useState } from 'react';
import { Badge, Progress, Accordion, AccordionItem, colors } from "@heroui/react";
import { ChevronDown, ChevronUp, Star, AlertTriangle, StarHalf, StarOff } from 'lucide-react';
import { Card, CardBody, CardHeader } from "@heroui/react";
import { company_google_reviews, company_scrapper_records } from '@prisma/client';

interface GoogleReviewProps {
    company_google_reviews: company_google_reviews;
    company_scrapper_records: company_scrapper_records;
}

const GoogleReview: React.FC<GoogleReviewProps> = ( data ) => {

    const company_google_review = data.company_google_reviews;

    const company_scrapper_records = data.company_scrapper_records;

    const getSentimentColor = (sentiment: string) => {
        switch (sentiment) {
            case 'Positive':
                return 'text-green-600';
            case 'Neutral':
                return 'text-yellow-600';
            case 'Unsatisfactory':
                return 'text-red-600';
            default:
                return 'text-gray-600';
        }
    };

    var convertToPercentage = (value: string | null) => {
        if (!value) return 0;
        try {
            var new_value = parseFloat(value);
        } catch (err) {
            value = value.replace('%', '');
            var new_value = parseFloat(value);
        }

        return new_value;
    };

    var getReversePercentage = (value: number) => {
        return 100 - value;
    };

    var convertWording = (value: string | null) => {
        if (!value) return '';
        if (value === 'Negative') {
            return 'Unsatisfactory';
        } else {
            return value;
        }
    };

    const reviewData: any = {
        averageRating: company_google_review.avg_rating,
        totalReviews: company_scrapper_records.number_of_reviews,
        complaintPercentage: convertToPercentage(company_google_review.complaint_percentage),
        scamRelatedComplaints: company_google_review.scam_related_complaints,
        normalComplaints: company_google_review.normal_complaints,
        overallSentiment: convertWording(company_google_review.sentiment_analysis),
    };

    // console.log(reviewData);

    const [isExpanded, setIsExpanded] = useState(false);

    const renderStars = (rating: number) => (
        <div className="ml-2 flex items-center">
            {[...Array(5)].map((_, index) => (
                <Star key={index} size={24} className={`${index < rating ? 'fill-yellow-500 text-yellow-500' : 'fill-gray-300 text-gray-300'}`} />
            ))}
        </div>
    );

    // const renderRatingBar = (rating: number, percentage: number) => (
    //     <div key={rating} className="my-1 flex items-center space-x-2">
    //         <div className="flex items-center">
    //             {[...Array(rating)].map((_, i) => (
    //                 <Star key={i} size={16} fill="#FFD700" color="#FFD700" />
    //             ))}
    //             {[...Array(5 - rating)].map((_, i) => (
    //                 <Star key={i} size={16} color="#E0E0E0" />
    //             ))}
    //         </div>
    //         <div className="h-2.5 flex-grow rounded-full bg-gray-200">
    //             <div className="h-2.5 rounded-full bg-primary-600" style={{ width: `${percentage}%` }} />
    //         </div>
    //         <span className="text-sm text-gray-600">{percentage}%</span>
    //     </div>
    // );
    if (reviewData.averageRating == null || reviewData.totalReviews == null) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-2">
                <p className="text-center text-gray-600">No reviews available</p>
            </div>
        );
    }

    if (
        reviewData.complaintPercentage == null ||
        reviewData.scamRelatedComplaints == null ||
        reviewData.normalComplaints == null ||
        reviewData.overallSentiment == null
    ) {
        return (
            <Card className="mx-auto flex w-full flex-col items-center justify-center bg-zinc-50" classNames={{base: 'bg-zinc-50'}}>
                <CardHeader className="flex flex-row items-center justify-center">
                    <svg
                        className="w-12"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        data-src="https://static.elfsight.com/icons/app-all-in-one-reviews-icons-google-multicolor.svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                    >
                        <path fill="#2A84FC" d="M21.579 12.234c0-.677-.055-1.359-.172-2.025h-9.403v3.839h5.384a4.615 4.615 0 0 1-1.992 3.029v2.49h3.212c1.886-1.736 2.97-4.3 2.97-7.333Z"></path>
                        <path fill="#00AC47" d="M12.004 21.974c2.688 0 4.956-.882 6.608-2.406l-3.213-2.491c-.893.608-2.047.952-3.392.952-2.6 0-4.806-1.754-5.597-4.113H3.095v2.567a9.97 9.97 0 0 0 8.909 5.491Z"></path>
                        <path fill="#FFBA00" d="M6.407 13.916a5.971 5.971 0 0 1 0-3.817V7.531H3.095a9.977 9.977 0 0 0 0 8.953l3.312-2.568Z"></path>
                        <path fill="#FC2C25" d="M12.004 5.982a5.417 5.417 0 0 1 3.824 1.494l2.846-2.846a9.581 9.581 0 0 0-6.67-2.593A9.967 9.967 0 0 0 3.095 7.53l3.312 2.57c.787-2.363 2.996-4.117 5.597-4.117Z"></path>
                    </svg>
                    <div className="flex flex-col items-center justify-center">
                        <h4>Google Reviews</h4>
                        <div className="mx-4 flex items-center">
                            {renderStars(Math.round(reviewData.averageRating))}
                            <h3 className="ml-2 text-lg font-bold">{reviewData.averageRating}</h3>
                        </div>
                        <div className="mt-2 flex items-center lg:mt-0">
                            <span className="mx-4 text-gray-600">{reviewData.totalReviews} total reviews</span>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Card className="mx-auto flex w-full flex-col items-center justify-center bg-zinc-50" classNames={{base: 'bg-zinc-50'}}>
            <CardHeader onClick={() => setIsExpanded(!isExpanded)} className="flex cursor-pointer flex-row items-center justify-center">
                <svg
                    className="w-12"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    data-src="https://static.elfsight.com/icons/app-all-in-one-reviews-icons-google-multicolor.svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <path
                        fill="#2A84FC"
                        d="M21.579 12.234c0-.677-.055-1.359-.172-2.025h-9.403v3.839h5.384a4.615 4.615 0 0 1-1.992 3.029v2.49h3.212c1.886-1.736 2.97-4.3 2.97-7.333Z"
                    ></path>
                    <path
                        fill="#00AC47"
                        d="M12.004 21.974c2.688 0 4.956-.882 6.608-2.406l-3.213-2.491c-.893.608-2.047.952-3.392.952-2.6 0-4.806-1.754-5.597-4.113H3.095v2.567a9.97 9.97 0 0 0 8.909 5.491Z"
                    ></path>
                    <path fill="#FFBA00" d="M6.407 13.916a5.971 5.971 0 0 1 0-3.817V7.531H3.095a9.977 9.977 0 0 0 0 8.953l3.312-2.568Z"></path>
                    <path
                        fill="#FC2C25"
                        d="M12.004 5.982a5.417 5.417 0 0 1 3.824 1.494l2.846-2.846a9.581 9.581 0 0 0-6.67-2.593A9.967 9.967 0 0 0 3.095 7.53l3.312 2.57c.787-2.363 2.996-4.117 5.597-4.117Z"
                    ></path>
                </svg>
                <div className="flex flex-col items-center justify-center">
                    <h4>Google Reviews</h4>
                    <div className="mx-4 flex items-center">
                        {renderStars(Math.round(reviewData.averageRating))}
                        <h3 className="ml-2 text-lg font-bold">{reviewData.averageRating}</h3>
                    </div>

                    <div className="mt-2 flex items-center lg:mt-0">
                        <span className="mx-4 text-gray-600">{reviewData.totalReviews} total reviews</span>
                    </div>
                </div>
                {isExpanded ? <ChevronUp /> : <ChevronDown />}
            </CardHeader>

            {isExpanded && (
                <CardBody>
                    <div className="space-y-4">
                        <div className="flex flex-col items-start justify-between lg:flex-row lg:space-x-8">
                            {/* Left side - Text content */}
                            <div className="">
                                <h4 className="mb-2 text-lg font-semibold">Sentiment Analysis of Reviews</h4>
                                <div className={`text-xl font-bold ${getSentimentColor(reviewData.overallSentiment)}`}>{reviewData.overallSentiment}</div>

                                <p className="text-sm font-semibold text-gray-600">
                                    {getReversePercentage(reviewData.complaintPercentage)}% of reviews reflect a neutral to positive sentiment.
                                </p>
                            </div>
                        </div>
                    </div>
                </CardBody>
            )}
        </Card>
    );
};

export default GoogleReview;
